import './style.scss'
import { Button, Dropdown, Input, message, Modal, Popover, Progress, Radio, Switch, Tooltip, Tour } from 'antd'
import { RcFile } from 'antd/es/upload'
import Dragger from 'antd/es/upload/Dragger'
import Upload from 'antd/es/upload/Upload'
import axios from 'axios'
import { Buffer } from 'buffer'
import { FC, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import * as uuid from 'uuid'
import wavConverter from 'wav-converter'
import { cdnApi, cmsApi } from '@/api'
import * as homeApi from '@/api/home'
import IconBg from '@/assets/icon-bg.png'
import IconSetting from '@/assets/icon-setting.png'
import RedPackage from '@/assets/red-package.gif'
import { PlayGray } from '@/assets/svg/play-gray'
import AudioDemos from '@/components/AudioDemos'
import CheckLogin, { openLoginModal } from '@/components/CheckLogin'
import PayPointModal from '@/components/PayPointModal'
import SystemGuide from '@/components/SystemGuide'
import UserCredits from '@/components/UserCredits'
import VoiceSettingModal from '@/components/VoiceSettingModal'
import { IUserPackage, UserStore } from '@/global-states'
import {
  checkSensitive,
  eventTracking,
  getAudioService,
  Render,
  showCouponModal,
  showPlanModal,
  translateHtml,
  urlOsskey,
  urlSource
} from '@/libs/util'
import {
  ArrowLeftOutlined,
  CloudUploadOutlined,
  CopyOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  FormOutlined,
  LoadingOutlined,
  PauseOutlined,
  PlusOutlined,
  RightOutlined
} from '@ant-design/icons'
import AvatarModal from './components/AvatarModal'
import BackgroundModal from './components/BackgroundModal'
import CaptionModal from './components/CaptionModal'
import RichText from './components/RichText'
import CreateVideoSetting from './components/Setting'
import VideoList from './components/VideoList'
import VoiceModal from './components/VoiceModal'
import { CreateVideoStore } from './create-video-state'

// const defaultText =
//   '从当下这一刻起，拒绝内耗。只因命运不会偏袒任何人，却会眷顾一直朝着光亮前进的人。我相信，路虽远，行则可至；事虽难，做则可成。一切好事将至，祝大家如愿以偿。'

const CreateVideo: FC = () => {
  const { t: translate } = useTranslation(['create_video'])
  const defaultText = translate('create_video.default_text')
  const { userPackage, payStatus, systemGuideStep } = UserStore
  const { model, highSetting, couponId } = CreateVideoStore
  const navigate = useNavigate()
  const params: any = useParams()
  const [searchParams] = useSearchParams()
  const [currentStep, setCurrentStep] = useState(0)
  const [audioFile, setAudioFile] = useState<RcFile>(undefined as any)
  const [audioUrl, setAudioUrl] = useState<string>(undefined as any)
  const [audioInfo, setAudioInfo] = useState<any>({})
  const [percent, setPercent] = useState(0)
  const cancelTokenSource = useRef<any>()
  const [currentTts, setCurrentTts] = useState<any>()
  const [currentAvatar, setCurrentAvatar] = useState<any>()
  const [textInputName, setTextInputName] = useState(translate('create_video.unnamed'))
  const [previewId, setPreviewId] = useState<any>(undefined)
  const prviewRef = useRef<any>()
  const [loading, setLoading] = useState(false)
  const audioRef = useRef<any>()
  const [payPointModalOpen, setPayPointModalOpen] = useState(false)
  const [edit, setEdit] = useState(false)
  const [settingPreview, setSettingPreview] = useState<any>()
  const [voiceModalOpen, setVoiceModalOpen] = useState(false)
  const [playingId, setPlayingId] = useState()
  const [avatarModalOpen, setAvatarModalOpen] = useState(false)
  const [segmentIndex, setSegmentIndex] = useState(0)
  const [segments, setSegments] = useState<any[]>([])
  const segmentsCurrent = useRef<any>()
  const [audioSelectAvatar, setAudioSelectAvatar] = useState<any>({})
  const [open, setOpen] = useState(false)
  const [highsettingOpen, setHighsettingOpen] = useState(false)
  const [backgroundModalOpen, setBackgroundModalOpen] = useState(false)
  const [captionModalOpen, setCaptionModalOpen] = useState(false)
  const [caption, setCaption] = useState<any>()
  const lastRef = useRef<any>()
  const videoListRef = useRef<any>()
  const guideRef = useRef<any>()
  const guideOpen = useRef<any>()
  const submitRef = useRef<any>()
  const captionModalRef = useRef<any>()
  const isBulk = useMemo(() => {
    return segments?.length > 1 && model === 'text'
  }, [segments, model])

  const steps: any = [
    {
      title: (
        <div className="guide-detail">
          <div className="title">{translate('create_video.guide_title')}</div>
          <div className="desc">{translate('create_video.guide_desc')}</div>
        </div>
      ),
      target: () => guideRef.current,
      closeIcon: false,
      placement: 'top'
    }
  ]

  useEffect(() => {
    if (systemGuideStep > 0) {
      guideOpen.current = true
    } else {
      guideOpen.current = false
    }
  }, [systemGuideStep])

  useEffect(() => {
    if (payStatus) {
      UserStore.updateUserPackage()
    }
  }, [payStatus])

  useEffect(() => {
    const isVip = userPackage?.membership_expire_time && !userPackage?.membership_expired
    if (userPackage && highSetting.mode === -1) {
      if (userPackage?.current_membership_level === 50) {
        CreateVideoStore.highSetting = { ...highSetting, mode: 1, model_version: isVip ? 1 : 0 }
      } else {
        CreateVideoStore.highSetting = { ...highSetting, mode: 0, model_version: isVip ? 1 : 0 }
      }
    }
  }, [userPackage, highSetting])

  const totalSeconds = useMemo(() => {
    const totalSeconds = segments?.reduce((pre, next) => {
      if (next.type === 1) {
        return pre + (next.duration || 0)
      } else {
        const s = translateHtml(next.words)?.length / 4.2
        return pre + s
      }
    }, 0)

    return Math.ceil(totalSeconds || 0)
  }, [segments])

  const showTime = useMemo(() => {
    const hours = Math.floor(totalSeconds / 3600)
    const minutes = Math.floor((totalSeconds % 3600) / 60)
    const remainingSeconds = totalSeconds % 60

    if (totalSeconds >= 0 && totalSeconds <= 60) {
      return translate('create_video.time_seconds', { seconds: totalSeconds })
    }
    if (totalSeconds > 60 && totalSeconds < 3600) {
      return translate('create_video.time_minutes_seconds', { minutes, seconds: remainingSeconds })
    }
    if (totalSeconds >= 3600) {
      return translate('create_video.time_hours_minutes_seconds', { hours, minutes, seconds: remainingSeconds })
    }
  }, [totalSeconds, translate])

  const timeThanLimit = useMemo(() => {
    if ((highSetting.mode === 0 && totalSeconds > 3600) || (highSetting.mode === 1 && totalSeconds > 300)) {
      return true
    }
    return false
  }, [totalSeconds, highSetting.mode])

  useEffect(() => {
    segmentsCurrent.current = segments
  }, [segments])

  useEffect(() => {
    ;(window as any).Buffer = Buffer
    const id = searchParams.get('id')
    setTextInputName(translate('create_video.unnamed'))
    restartUpload()
    UserStore.initSensitiveWords()
    getOpenConfig()

    if (id) {
      restoreData(+id)
    } else {
      initData()
    }

    return () => {
      prviewRef.current = undefined
      closeAudio()
      sessionStorage.removeItem('bookId')
    }
  }, [])

  useEffect(() => {
    if (segments?.length && lastRef.current) {
      lastRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }, [segments?.length, lastRef])

  useEffect(() => {
    if (audioFile) {
      setCurrentStep(1)
      setAudioUrl(URL.createObjectURL(audioFile))
    } else {
      setAudioUrl(undefined as any)
    }
  }, [audioFile])

  useEffect(() => {
    prviewRef.current = previewId
  }, [previewId])

  const saveName = () => {
    if (!textInputName?.trim()) {
      setTextInputName(translate('create_video.unnamed'))
    }
    setEdit(false)
  }

  const restoreData = async (id: number) => {
    const res = await homeApi.getCreationDetail(id)
    if (res?.origin_request) {
      const humans = res.digital_human_infos || []
      const voices = res.tts_voice_infos || []
      const origin = res.origin_request || {}
      const bgInfos = res.background_picture_infos
      const alphaInfos = res.digital_human_alpha_preview_infos

      const attachment = JSON.parse(origin.attachment || '{}')
      const { audio_oss_key, text_to_audio } = origin

      if (audio_oss_key) {
        setAudioInfo({ oss_key: audio_oss_key })
        setAudioUrl(urlOsskey(audio_oss_key))
        setCurrentStep(2)
        CreateVideoStore.model = 'audio'
        initData()
      } else if (text_to_audio) {
        // 小程序数据特殊处理
        CreateVideoStore.model = 'text'
        setSegments([
          {
            defaultText: text_to_audio.text,
            avatar: humans?.[0],
            voice: voices?.[0],
            words: text_to_audio.text,
            type: 2
          }
        ])
      } else {
        CreateVideoStore.model = 'text'
        setSegments(
          attachment.segments?.map((s: any) => {
            const bg: any = {}
            const id = s.avatar?.bgId
            if (id) {
              const bgRes = bgInfos?.find((f: any) => f.id === id)
              const alphaRes = alphaInfos?.find((f: any) => f.digital_human_id === s.avatar?.id)
              bg.bgId = bgRes?.id
              bg.bgUrl = bgRes?.url
              bg.alphaUrl = alphaRes?.alpha_preview_url
            }

            if (s.type === 1) {
              return {
                ...s,
                avatar: {
                  ...(humans.find((h: any) => h.id === s.avatar?.id) || {}),
                  ...bg
                },
                audioFile: urlOsskey(s.audio_oss_key)
              }
            } else {
              return {
                ...s,
                ...bg,
                defaultText: s.words,
                avatar: {
                  ...(humans.find((h: any) => h.id === s.avatar?.id) || {}),
                  ...bg
                },
                voice: voices.find((v: any) => v.name === s.voice?.name) || {}
              }
            }
          })
        )
      }

      setAudioSelectAvatar(humans?.[0])
      CreateVideoStore.highSetting = {
        drive_mode: origin.drive_mode || 0,
        only_generate_audio: origin.only_generate_audio || false,
        mode: origin.mode || 0,
        model_version: origin.model_version || 0
      }
    }
  }

  const initData = async () => {
    const voice = await initVoice()
    const avatar = await initAvatar()
    setSegments([
      {
        id: uuid.v4(),
        avatar,
        voice: avatar.tts_voice || voice,
        words: defaultText,
        type: 2,
        defaultText
      }
    ])
  }

  const initVoice = async () => {
    const voiceId = localStorage.getItem('voiceId') || ''
    const bookId = sessionStorage.getItem('bookId')

    let speakers: any[] = []

    if (bookId) {
      const [books] = await Promise.all([homeApi.getCommunityVoiceList(+bookId)])
      speakers = books?.list || []
    } else {
      const [global, self] = await Promise.all([getGlobalTts(), getSelfTts()])
      speakers = [...self, ...global]?.filter((i) => i.status === 1) || []
    }

    const account = await UserStore.updateUserPackage()
    const level = account?.current_membership_level || 0
    // 过滤掉不可用的精品声音和高保真
    const canUseSpeakers = speakers.filter((s) => {
      if (s.is_premium && level < 10) {
        return false
      }
      if (s.level === 20 && level < 20) {
        return false
      }
      return true
    })
    const findVoice = canUseSpeakers.find((s) => s.id === +voiceId)
    let voice: any
    if (voiceId && findVoice) {
      voice = findVoice
    } else {
      voice = canUseSpeakers[0]
    }

    return voice
  }

  const initAvatar = async () => {
    let avatars: any[] = []
    const avatarId = params.avatarId || localStorage.getItem('digitalHumanId')

    if (avatarId) {
      const [avatarDetail] = await Promise.all([homeApi.getDigitalHumanDetails(+avatarId)])
      const { digital_human, tts_voice } = avatarDetail
      if (digital_human) {
        avatars = [
          {
            ...digital_human,
            tts_voice: tts_voice
          }
        ]
      } else {
        const [humans, globalHumans] = await Promise.all([homeApi.getDigitalHumans(), homeApi.getGlobalDigitalHumans()])
        avatars = [...(humans?.list || []).filter((d: any) => d.status === 2), ...(globalHumans?.list || [])]
      }
    } else {
      const [humans, globalHumans] = await Promise.all([homeApi.getDigitalHumans(), homeApi.getGlobalDigitalHumans()])
      avatars = [...(humans?.list || []).filter((d: any) => d.status === 2), ...(globalHumans?.list || [])]
    }

    const findAvatar = avatars?.find((s) => s.id === +avatarId)
    const renderAvatar = findAvatar ? findAvatar : avatars?.[0]
    if (!searchParams.get('id')) {
      setAudioSelectAvatar(renderAvatar)
    }
    return renderAvatar
  }

  const getOpenConfig = async () => {
    const res = await cdnApi.get(`hiflys/create-video/config.json`)
    if (res.open === true) {
      setOpen(true)
    }
  }

  const getGlobalTts = async () => {
    const res = await homeApi.getGlobalTts()
    return res?.list || []
  }

  const getSelfTts = async () => {
    const res = await homeApi.getSelfTts()
    return res.list || []
  }

  const uploadFile = async (
    file: RcFile,
    onUploadProgress?: (p: any) => void,
    onUploadSuccess?: (url: any) => void
  ) => {
    try {
      const segs = (file.name || '').split(/\./)
      const { upload_url, oss_key, content_type } =
        (await cmsApi.post('upload_url', {
          extension: segs[segs.length - 1],
          media_type: 2
        })) || {}
      if (!upload_url) {
        throw new Error('failed to upload file')
      }

      cancelTokenSource.current = axios.CancelToken.source()
      onUploadProgress?.(0)

      await cmsApi.upload(upload_url.replace(/^http:\/\//, 'https://').replace('-internal', ''), file, {
        onUploadProgress: (progress) => {
          const percent = Math.round((progress.progress || 0) * 100)
          onUploadProgress?.(percent)
        },
        headers: {
          'Content-Type': content_type
        },
        cancelToken: cancelTokenSource.current?.token
      })

      onUploadSuccess?.(oss_key)
    } catch (err: any) {
      if (err?.code !== 'ERR_CANCELED') {
        message.error(err?.message || err)
      }
    }
  }

  const beforeAudioUpload = async (file: RcFile) => {
    return new Promise((resolve) => {
      const type = file?.name?.split(/\./)?.slice(-1)?.[0]?.toLocaleLowerCase() as string
      if (['wav', 'mp3', 'm4a'].includes(type)) {
        const audioElement = document.createElement('audio')
        audioElement.src = URL.createObjectURL(file)
        audioElement.addEventListener('loadedmetadata', () => {
          const duration = audioElement.duration
          console.log('videoElement', duration)
          if (duration < 5 || duration > 1800) {
            message.warning(translate('create_video.audio_duration_warning'))
            resolve(false)
          }
          resolve(duration)
        })
      } else {
        message.warning(translate('create_video.audio_format_warning'))
        resolve(false)
      }
    })
  }

  const onDrop = async (file: RcFile) => {
    const type = file?.name?.split(/\./)?.slice(-1)?.[0]?.toLocaleLowerCase() as string
    if (!['wav', 'mp3', 'm4a'].includes(type)) {
      message.warning(translate('create_video.audio_format_warning'))
    }
  }

  const restartUpload = () => {
    cancelUpload()
    setCurrentStep(0)
    setPercent(0)
    setAudioFile(undefined as any)
    setAudioInfo(undefined as any)
  }

  const cancelUpload = () => {
    if (cancelTokenSource) {
      cancelTokenSource.current?.cancel(translate('create_video.upload_canceled'))
    }
  }

  const showMembershipTip = () => {
    Modal.confirm({
      title: translate('create_video.tip_title'),
      content: <div>{translate('create_video.membership_tip_content')}</div>,
      okText: translate('create_video.become_member'),
      cancelText: translate('create_video.continue_submit'),
      onOk: () => {
        showPlanModal()
      },
      onCancel: () => {
        completeCreate()
      }
    })
  }

  const checkPermission = () => {
    const level = userPackage?.current_membership_level || 0
    if (level < 10 && localStorage.getItem('token') && !guideOpen.current) {
      if (userPackage?.membership_expired && !localStorage.getItem('membership_expired')) {
        localStorage.setItem('membership_expired', '1')
        showMembershipTip()
      } else if (!localStorage.getItem('membership_unrecharge')) {
        localStorage.setItem('membership_unrecharge', '1')
        showMembershipTip()
      } else {
        completeCreate()
      }
    } else {
      completeCreate()
    }
  }

  const completeCreate = async (agree_to_truncate?: boolean) => {
    if (model === 'text') {
      const errorWords = checkSensitive(segments?.filter((s) => s.type === 2) || [])
      if (errorWords?.length) {
        setLoading(false)
        eventTracking('sensitiveWords', {
          sensitiveWord: errorWords.join('、')
        })
        return message.error(
          <div style={{ padding: '12px' }}>
            {translate('create_video.sensitive_words_error', { words: errorWords.join('、') })}
          </div>
        )
      }

      const index = segments?.findIndex((s) => s?.type === 2 && s?.voice?.model === 8 && s?.voice?.level === 10)
      const totalText = segments?.reduce((pre, next) => {
        const s = translateHtml(next.words)?.length
        return pre + s
      }, 0)

      if (index > -1 && Math.ceil(totalText) > 260) {
        return unlockVoice(index)
      }
    }

    try {
      setLoading(true)
      const res = await homeApi.authRotate({
        seed: uuid.v4(),
        replace: true
      })

      const checkRes = await UserStore.checkUserPackage()

      if (checkRes) return

      let postRes

      await eventTracking('submitCreation')

      // 缓存数字人id
      const digitalHumanId = model === 'text' ? segments?.[0]?.avatar?.id : audioSelectAvatar?.id
      if (digitalHumanId) {
        localStorage.setItem('digitalHumanId', digitalHumanId)
      }

      const postData: any = {
        type: 0,
        title: textInputName,
        client_type: 1,
        drive_mode: highSetting.drive_mode,
        only_generate_audio: model === 'text' ? highSetting.only_generate_audio : false,
        mode: highSetting.mode,
        model_version: highSetting.model_version,
        agree_to_truncate,
        di: systemGuideStep === 5 ? UserStore.device_id : undefined
      }

      if (model === 'text') {
        postData.segments = segments?.map((s) => {
          if (s.type === 1) {
            return {
              type: s.type,
              digital_human_id: s.avatar.id,
              audio_oss_key: s.audio_oss_key,
              background_picture_id: s.avatar.bgId,
              digital_human_width: s.digital_human_width,
              digital_human_height: s.digital_human_height
            }
          } else {
            return {
              type: s.type,
              digital_human_id: s.avatar.id,
              voice_name: s.voice.name,
              source_text: translateHtml(s.words),
              background_picture_id: s.avatar.bgId,
              subtitle: s.subtitle,
              digital_human_width: s.digital_human_width,
              digital_human_height: s.digital_human_height
            }
          }
        })
      } else {
        postData.segments = [
          {
            type: 1,
            digital_human_id: audioSelectAvatar.id,
            audio_oss_key: audioInfo.oss_key,
            background_picture_id: audioSelectAvatar.bgId
          }
        ]
      }

      if (submitRef.current === 'bulk' && model === 'text') {
        postData.attachments = segments?.map((s) => {
          return JSON.stringify({ segments: [s] })
        })
        postRes = await homeApi.bulkAddCreations(res.server_time, postData)
      } else {
        postData.attachment = JSON.stringify({
          segments:
            model === 'text'
              ? segments
              : [
                  {
                    type: 1,
                    id: uuid.v4(),
                    avatar: audioSelectAvatar,
                    audio_oss_key: audioInfo.oss_key
                  }
                ]
        })
        postRes = await homeApi.addCreations(res.server_time, postData)
      }

      if (postRes.sensitive_words) {
        return Modal.error({
          title: translate('create_video.submit_failed'),
          content: (
            <div>
              {translate('create_video.sensitive_content_error', { words: postRes.sensitive_words.join('、') })}
            </div>
          )
        })
      }

      successModalTip()
    } catch (error: any) {
      if (error.code === 1002) {
        setPayPointModalOpen(true)
      }
      if (error.code === 1003 || error.code === 1004 || error.code === 1005) {
        showPlanModal()
      }

      if (error.code === 1026) {
        Modal.confirm({
          title: translate('create_video.premium_member_feature'),
          content: <div>{translate('create_video.bulk_generate_premium')}</div>,
          okText: translate('create_video.upgrade_premium'),
          onOk: () => {
            showPlanModal()
          }
        })
      }

      if (error.code === 1023) {
        showTruncateConfirm()
      }
    } finally {
      setLoading(false)
    }
  }

  const showTruncateConfirm = () => {
    Modal.confirm({
      title: translate('create_video.tip_title'),
      content: (
        <div>
          {translate('create_video.truncate_confirm', {
            type: model === 'text' ? translate('create_video.text') : translate('create_video.audio')
          })}
        </div>
      ),
      footer: (_, { OkBtn }) => (
        <>
          <Button
            onClick={() => {
              eventTracking('AutoTruncate')
              Modal.destroyAll()
              completeCreate(true)
            }}
          >
            {translate('create_video.auto_truncate')}
          </Button>
          <OkBtn />
        </>
      ),
      okText: translate('create_video.become_member'),
      closable: true,
      onOk: () => {
        showPlanModal()
      }
    })
  }

  const unlockVoice = async (index: number) => {
    const res: IUserPackage = await UserStore.updateUserPackage()
    const totalPoint = (res.membership_credits || 0) + (res.permanent_credits || 0) + (res.free_credits || 0)
    if ((res?.current_membership_level || 0) < 20) {
      return Modal.confirm({
        width: 500,
        title: translate('create_video.v2_trial_restriction'),
        content: <div>{translate('create_video.v2_trial_restriction_content')}</div>,
        okText: translate('create_video.upgrade_premium'),
        cancelText: translate('create_video.cancel'),
        onOk: () => {
          showPlanModal()
        }
      })
    }

    const instance = Modal.confirm({
      width: 560,
      title: translate('create_video.v2_unlock'),
      content: (
        <div>
          <div>{translate('create_video.v2_unlock_content')}</div>
          <div style={{ marginTop: 12 }}>{translate('create_video.v2_unlock_points', { points: 5000 })}</div>
          <div style={{ marginTop: 6 }}>{translate('create_video.your_points', { points: totalPoint })}</div>
          <div style={{ marginTop: 2 }}>{translate('create_video.points_consumed', { points: 5000 })}</div>
        </div>
      ),
      okText: translate('create_video.confirm_unlock'),
      cancelText: translate('create_video.cancel'),
      onOk: async () => {
        await unlock()
      }
    })

    const unlock = async () => {
      instance.update({
        okButtonProps: {
          loading: true
        },
        okText: translate('create_video.unlocking'),
        cancelButtonProps: {
          disabled: true
        }
      })
      const res = await homeApi.purchaseVoice({
        trial_id: segments?.[index]?.voice?.id
      })
      const updateSegments = [...segments]
      updateSegments[index].voice.level = 20
      updateSegments[index].voice.model = 102
      updateSegments[index].voice.name = res.voice_name
      setSegments(updateSegments)
      message.success(translate('create_video.unlock_success'))
      return
    }
  }

  const setGuideRead = () => {
    if (systemGuideStep) {
      eventTracking('SystemGuideNextStep5')
      UserStore.systemGuideStep = undefined
      localStorage.setItem('systemGuideRead', '1')
    }
  }

  const submitForm = () => {
    setGuideRead()
    if (model === 'text') {
      submitTextCheck()
    } else {
      submitAuduioCheck()
    }
  }

  const submitTextCheck = () => {
    if (segments?.some((s) => !translateHtml(s.words) && s.type === 2)) {
      return message.warning(translate('create_video.enter_dialogue'))
    }

    if (segments?.some((s) => s.words && s.type === 2) && totalSeconds < 2) {
      return message.warning(translate('create_video.duration_too_short'))
    }

    if (!userPackage?.membership_expire_time && totalSeconds > 45) {
      return Modal.confirm({
        title: translate('create_video.tip_title'),
        content: <div>{translate('create_video.free_user_limit', { seconds: 45 })}</div>,
        onOk: () => {
          showPlanModal()
        },
        cancelText: translate('create_video.acknowledge_and_modify'),
        okText: translate('create_video.become_member')
      })
    }

    if (timeThanLimit) {
      if (highSetting.mode === 0) {
        return Modal.warning({
          title: translate('create_video.tip_title'),
          content: <div>{translate('create_video.points_mode_limit', { minutes: 60 })}</div>,
          okText: translate('create_video.acknowledge_and_modify')
        })
      } else {
        return Modal.warning({
          title: translate('create_video.tip_title'),
          content: <div>{translate('create_video.dark_mode_limit', { seconds: 300 })}</div>,
          okText: translate('create_video.acknowledge_and_modify')
        })
      }
    }
    checkPermission()
  }

  const submitAuduioCheck = () => {
    if (!audioInfo?.oss_key) {
      return message.warning(translate('create_video.upload_audio_first'))
    }
    checkPermission()
  }

  const onChange = (index: number, text: string) => {
    const updateSegments = [...segments]
    updateSegments[index].words = text
    if (!text?.replace(/<[^>]*>/g, '')) {
      updateSegments[index].defaultText = ''
    }
    setSegments(updateSegments)
  }

  const previewWords = async (index: number, words: string) => {
    const voice = segments[index].voice

    if (![1, 2, 3, 4].includes(voice.model)) {
      const audioService = getAudioService()
      audioService.startPreview({
        is_community: false,
        id: voice.id,
        text: words,
        voice_parameters: voice.voice_parameters
      })
      return audioService
    } else {
      return await previewTts(segments[index].voice, words)
    }
  }

  const previewTts = async (tts: any, text: string) => {
    if (playingId === tts?.name) {
      closeAudio()
      return
    } else if (previewId === tts.id) {
      return
    }

    setPreviewId(tts.name)
    setPlayingId(undefined)
    clearAudio()

    try {
      let res
      if (tts.parent_id || tts.is_community) {
        res = await homeApi.previewCommunityVoice(tts.parent_id, tts.id, {
          text,
          voice_parameters: tts.voice_parameters
        })
      } else {
        res = await homeApi.previewTts(tts.id, { text, voice_parameters: tts.voice_parameters })
      }
      if (prviewRef.current !== tts.name) {
        return
      }
      setPlayingId(tts.name)

      clearAudio()

      const audio = new Audio()
      audio.src = `data:audio/wav;base64,${wavConverter
        .encodeWav(new Buffer(res.audio_base64, 'base64'), {
          numChannels: 1,
          sampleRate: 16000,
          byteRate: 32_000
        })
        .toString('base64')}`
      audio.play()
      audioRef.current = audio

      audio.addEventListener('ended', () => {
        closeAudio()
        CreateVideoStore.previewEnd = true
      })

      audio.addEventListener('pause', function () {
        closeAudio()
        CreateVideoStore.previewEnd = true
      })
    } catch {
      closeAudio()
    }
  }

  const clearAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause?.()
      audioRef.current.src = ''
    }
  }

  const closeAudio = () => {
    clearAudio()
    setPlayingId(undefined)
    setPreviewId(undefined)
  }

  const successModalTip = () => {
    message.success(translate('create_video.submit_success'))
    updateInfos()
  }

  const selectDemoAudio = (v: any) => {
    setAudioInfo({
      oss_key: v.ossKey
    })
    setAudioUrl(urlSource(v.url))
    setCurrentStep(2)
  }

  const openPlan = () => {
    showPlanModal()
  }

  const addVoiceSegment = (file: RcFile) => {
    const lastSegment = segments?.at(-1)
    setSegmentIndex(segments?.length)
    const newSegment = [
      ...(segments || []),
      {
        voice: lastSegment.voice,
        avatar: lastSegment.avatar,
        audioFile: URL.createObjectURL(file),
        type: 1,
        id: uuid.v4()
      }
    ]
    setSegments(newSegment)
    return newSegment
  }

  const addTextSegment = async () => {
    const lastSegment = segments?.at(-1)
    if (!lastSegment?.voice) {
      const voice = await initVoice()
      lastSegment.voice = voice
    }
    setSegments([
      ...(segments || []),
      {
        ...lastSegment,
        type: 2,
        id: uuid.v4(),
        words: '',
        defaultText: '',
        duration: 0
      }
    ])
  }

  const onTextChange = (text: string) => {
    const updateSegments = [...segments]
    updateSegments[segmentIndex].words = text
    updateSegments[segmentIndex].defaultText = text
    setSegments(updateSegments)
  }

  const onVoiceChange = (voice: any) => {
    localStorage.setItem('voiceId', voice.id)
    const updateSegments = [...segments]
    updateSegments[segmentIndex].voice = voice
    setSegments(updateSegments)
  }

  const updateVoiceSetting = (params: any) => {
    const updateSegments = [...segments]
    updateSegments[segmentIndex].voice.voice_parameters = params
    setSegments(updateSegments)
  }

  const onAvatarChange = async (avatar: any, naturalSize?: any) => {
    if (model === 'text') {
      const updateSegments = [...segments]
      if (naturalSize) {
        const res = await homeApi.getDigitalHumanDetails(avatar.id)
        if (res?.tts_voice) {
          updateSegments[segmentIndex].voice = res.tts_voice
        }
        if (updateSegments[segmentIndex].subtitle) {
          initCaption(naturalSize, segmentIndex, true)
        }
      }

      updateSegments[segmentIndex].avatar = avatar
      setSegments(updateSegments)
      if (segmentIndex === 0) {
        window.history.replaceState({}, '', `${location.origin}/create-video/${avatar.id}`)
      }
    } else {
      setAudioSelectAvatar(avatar)
    }
  }

  const oncCaptchaChange = (subtitle: any) => {
    const updateSegments = [...segments]
    updateSegments[segmentIndex].subtitle = subtitle
    setSegments(updateSegments)
  }

  const deleteSegment = (index: number) => {
    setSegments(segments?.filter((_, i) => index !== i))
  }

  const copySegment = (segment: any) => {
    setSegments((s) => [
      ...s,
      {
        ...segment,
        id: uuid.v4(),
        defaultText: segment.words
      }
    ])
  }

  const updateInfos = () => {
    videoListRef.current?.updateVideos()
  }

  const initCaption = (size: any, index: number, checked: boolean) => {
    let subtitle
    if (checked) {
      const { naturalWidth, naturalHeight } = size
      const fontSize = 72
      const height = fontSize * 1.5 * (Math.max(naturalWidth, naturalHeight) / 1920)
      const st = segments[index]?.subtitle || {}
      const pos_x_ratio = st?.pos_x_ratio ?? 0.1
      const pos_y_ratio = st?.pos_y_ratio ?? 0.85
      const width_ratio = st?.width_ratio ?? 0.8

      subtitle = {
        font_name: st?.font_name || 'Douyin Sans',
        font_size: st?.font_size || fontSize,
        primary_color: st?.primary_color || '#FFFFFF',
        outline_color: st?.outline_color || '#000000',
        width: Math.floor(naturalWidth * width_ratio),
        height: Math.floor(height),
        pos_x: Math.floor(naturalWidth * pos_x_ratio),
        pos_y: Math.floor(naturalHeight * pos_y_ratio),
        width_ratio,
        height_ratio: height / naturalHeight,
        pos_x_ratio,
        pos_y_ratio,
        ass_ratio: 1920 / Math.max(naturalWidth, naturalHeight)
      }
      captionModalRef.current.initFont()
    }
    const updateSegments = [...segments]
    updateSegments[index].subtitle = subtitle
    setSegments(updateSegments)
  }

  return (
    <div className="page-create page-create-video">
      <div className="page-header">
        <div className="title">
          <span className="icon" onClick={() => navigate(-1)}>
            <ArrowLeftOutlined />
          </span>
          <label>{translate('create_video.create_work')}</label>
        </div>

        {!!couponId && (
          <div
            className="gif"
            onClick={() => {
              showCouponModal()
            }}
          >
            <img src={RedPackage} />
          </div>
        )}

        <UserCredits />
      </div>

      <div className="create-content">
        <div className="left">
          <div className="rename">
            {edit ? (
              <Input
                autoFocus
                maxLength={20}
                value={textInputName}
                onChange={(e) => setTextInputName(e.target.value)}
                onBlur={saveName}
                onPressEnter={saveName}
              ></Input>
            ) : (
              <label className="name">
                <label className="text ellipsis">{textInputName}</label>
                <FormOutlined onClick={() => setEdit(true)} />
              </label>
            )}
          </div>
          <div className="tabs">
            <Radio.Group
              value={model}
              onChange={(e) => {
                CreateVideoStore.model = e.target.value
              }}
              buttonStyle="solid"
            >
              <Radio.Button value="text">{translate('create_video.text_driven')}</Radio.Button>
              <Radio.Button value="audio">{translate('create_video.audio_driven')}</Radio.Button>
            </Radio.Group>
            {!!segments?.length && model === 'text' && (
              <div className="stat">
                <span>
                  <Popover
                    content={
                      <div className="common-popover">
                        <div>{translate('create_video.duration_estimate_note')}</div>
                        <ul>
                          <li>
                            {translate('create_video.points_mode_limit_full', {
                              seconds: 3600,
                              minutes: 60,
                              words: 15000
                            })}
                          </li>
                          <li>
                            {translate('create_video.dark_mode_limit_full', {
                              seconds: 180,
                              words: 750
                            })}
                          </li>
                        </ul>
                      </div>
                    }
                    placement="right"
                  >
                    <ExclamationCircleOutlined />
                  </Popover>
                  {translate('create_video.estimated_duration')}：
                </span>
                {timeThanLimit ? (
                  <span className="time red">{showTime}</span>
                ) : (
                  <span className="time gradient">{showTime}</span>
                )}
              </div>
            )}
          </div>

          <div className="step-content" style={{ display: model === 'text' ? 'flex' : 'none' }}>
            {segments?.map((s: any, index: number) => (
              <div className="content-box" key={s.id}>
                <Render>
                  {function Segment() {
                    const [naturalSize, setNaturalSize] = useState<any>({})
                    return (
                      <>
                        <div className="photo">
                          {s.avatar.bgId ? (
                            <>
                              {!!s.avatar?.alphaUrl && <img src={s.avatar?.alphaUrl} />}
                              {!!s.avatar?.bgUrl && <img className="bg" src={s.avatar?.bgUrl} />}
                            </>
                          ) : (
                            <img
                              src={urlSource(s.avatar?.video_url, 'video')}
                              onLoad={(e: any) => {
                                const w = e.target?.naturalWidth
                                const h = e.target?.naturalHeight
                                setNaturalSize({ naturalWidth: w, naturalHeight: h })
                                const updateSegments = [...segments]
                                updateSegments[index].digital_human_width = w
                                updateSegments[index].digital_human_height = h
                                setSegments(updateSegments)
                              }}
                            />
                          )}

                          <div
                            className="op"
                            onClick={() => {
                              setCurrentAvatar(s.avatar)
                              setSegmentIndex(index)
                              setBackgroundModalOpen(true)
                            }}
                          >
                            <img src={IconBg} />
                            <span>{translate('create_video.change_background')}</span>
                          </div>
                          <Button
                            type="primary"
                            onClick={() => {
                              setCurrentAvatar(s.avatar)
                              setSegmentIndex(index)
                              setAvatarModalOpen(true)
                            }}
                          >
                            {translate('create_video.change')}
                          </Button>
                        </div>
                        <div className="segments-right">
                          {s.type !== 1 ? (
                            <>
                              <RichText
                                defaultText={s.defaultText || ''}
                                onChange={(text: string) => onChange(index, text)}
                                onPreviewWords={(words: string) => previewWords(index, words)}
                                closeAudio={closeAudio}
                              />

                              <div className="choose-voice">
                                <div className="choose-voice__left">
                                  <div className="left-img">
                                    <img src={s.voice?.group_cover_url} />
                                  </div>
                                  <div
                                    className="play-pause"
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      previewTts(
                                        s.voice,
                                        s.voice?.preview_text || translate('systm.default_preview_text')
                                      )
                                    }}
                                  >
                                    {previewId === s.voice?.name ? (
                                      playingId === s.voice?.name ? (
                                        <PauseOutlined />
                                      ) : (
                                        <LoadingOutlined />
                                      )
                                    ) : (
                                      <PlayGray className="play-icon" />
                                    )}
                                  </div>
                                  <div className="detail">
                                    <div className="name ellipsis">{s.voice?.group_title}</div>
                                    <div className="style ellipsis">{s.voice?.display_name}</div>
                                  </div>
                                </div>
                                <div className="choose-voice__right">
                                  <Button
                                    type="primary"
                                    className="black"
                                    onClick={() => {
                                      setSegmentIndex(index)
                                      setVoiceModalOpen(true)
                                      setCurrentTts(s.voice)
                                    }}
                                  >
                                    {translate('create_video.change_voice')}
                                  </Button>
                                  {s.voice?.level === 20 && (
                                    <Button className="voice-change" onClick={() => setSettingPreview(s.voice)}>
                                      {translate('create_video.adjust_params')}
                                    </Button>
                                  )}
                                </div>
                              </div>
                            </>
                          ) : (
                            <div className="segments-voice">
                              <audio controls src={s.audioFile} />
                              {!s.audio_oss_key && (
                                <div className="segment-mask">
                                  <LoadingOutlined />
                                  <div>
                                    {translate('create_video.uploading')} {s.percent}%
                                  </div>
                                </div>
                              )}
                            </div>
                          )}

                          {s.type !== 1 && (
                            <div className="segment-bottom">
                              <div className="caption">
                                <span>{translate('create_video.show_subtitle')}</span>
                                <Switch checked={!!s.subtitle} onChange={(v) => initCaption(naturalSize, index, v)} />

                                {!!s.subtitle && (
                                  <span
                                    onClick={() => {
                                      setCurrentAvatar(s.avatar)
                                      setCaption(s.subtitle)
                                      setSegmentIndex(index)
                                      setCaptionModalOpen(true)
                                    }}
                                    className="caption-setting gradient"
                                  >
                                    {translate('create_video.adjust_subtitle_style')}
                                    <RightOutlined />
                                  </span>
                                )}
                              </div>
                            </div>
                          )}

                          <div className="segment-op">
                            <div className="op-icon" onClick={() => copySegment(s)}>
                              <Tooltip placement="top" title={translate('create_video.copy')}>
                                <CopyOutlined />
                              </Tooltip>
                            </div>
                            {segments?.length > 1 && (
                              <div className="op-icon" onClick={() => deleteSegment(index)}>
                                <Tooltip placement="top" title={translate('create_video.delete')}>
                                  <DeleteOutlined />
                                </Tooltip>
                              </div>
                            )}
                          </div>
                        </div>
                      </>
                    )
                  }}
                </Render>
              </div>
            ))}

            {!!segments?.length && open && (
              <div className="segments-add">
                {UserStore.phone ? (
                  <Upload
                    accept=".mp3,.m4a,.wav"
                    showUploadList={false}
                    beforeUpload={async (file) => {
                      const duration = (await beforeAudioUpload(file)) as any
                      if (duration) {
                        const newSegment = addVoiceSegment(file)
                        const id = newSegment.at(-1)?.id
                        uploadFile(
                          file,
                          (percent) => {
                            const index = segmentsCurrent.current.findIndex((s: any) => s.id === id)
                            const updateSegments = [...segmentsCurrent.current]
                            updateSegments[index].percent = percent
                            setSegments(updateSegments)
                          },
                          (audio_oss_key) => {
                            const index = segmentsCurrent.current.findIndex((s: any) => s.id === id)
                            const updateSegments = [...segmentsCurrent.current]
                            updateSegments[index].duration = duration
                            updateSegments[index].percent = 100
                            updateSegments[index].audio_oss_key = audio_oss_key
                            setSegments(updateSegments)
                          }
                        )
                      }
                      return !!duration
                    }}
                  >
                    <Button icon={<PlusOutlined />} color="primary" variant="outlined">
                      {translate('create_video.add_audio')}
                    </Button>
                  </Upload>
                ) : (
                  <CheckLogin onLoginSuccess={updateInfos}>
                    <Button icon={<PlusOutlined />} color="primary" variant="outlined">
                      {translate('create_video.add_audio')}
                    </Button>
                  </CheckLogin>
                )}

                <Button icon={<PlusOutlined />} color="primary" variant="outlined" onClick={addTextSegment}>
                  {translate('create_video.add_text')}
                  <div className="tag">new</div>
                </Button>
              </div>
            )}

            <div ref={lastRef}></div>
          </div>

          <div className="step-content audio" style={{ display: model !== 'text' ? 'flex' : 'none' }}>
            <div className="content-box">
              <div className="photo">
                {audioSelectAvatar?.bgId ? (
                  <>
                    {!!audioSelectAvatar?.alphaUrl && <img src={audioSelectAvatar?.alphaUrl} />}
                    {!!audioSelectAvatar?.bgUrl && <img className="bg" src={audioSelectAvatar?.bgUrl} />}
                  </>
                ) : (
                  <img src={urlSource(audioSelectAvatar?.video_url_v2 || audioSelectAvatar?.video_url, 'video')} />
                )}

                <Button
                  type="primary"
                  onClick={() => {
                    setCurrentAvatar(audioSelectAvatar)
                    setAvatarModalOpen(true)
                  }}
                >
                  {translate('create_video.change')}
                </Button>

                <div
                  className="op"
                  onClick={() => {
                    setCurrentAvatar(audioSelectAvatar)
                    setBackgroundModalOpen(true)
                  }}
                >
                  <img src={IconBg} />
                  <span>{translate('create_video.change_background')}</span>
                </div>
              </div>
              <div className="audio-right">
                <div className="warning">
                  <h4>{translate('create_video.audio_requirements')}</h4>
                  <div className="desc">
                    <div>
                      <label className="label">{translate('create_video.file_format')}：</label>
                      <label>mp3、m4a、wav</label>
                    </div>
                    <div>
                      <label className="label">{translate('create_video.audio_duration')}：</label>
                      <label>{translate('create_video.duration_range', { min: 5, max: 1800 })}</label>
                    </div>
                  </div>
                </div>

                <div className="content">
                  {currentStep === 0 && (
                    <CheckLogin onLoginSuccess={updateInfos}>
                      <Dragger
                        accept=".mp3,.m4a,.wav"
                        showUploadList={false}
                        beforeUpload={async (file) => {
                          if (!UserStore.phone) {
                            return openLoginModal()
                          }
                          const flag = (await beforeAudioUpload(file)) as any
                          if (flag) {
                            setAudioFile(file)
                            uploadFile(
                              file,
                              (percent) => {
                                if (percent === 0) {
                                  setCurrentStep(1)
                                }
                                setPercent(percent)
                              },
                              (oss_key) => {
                                setCurrentStep(2)
                                setAudioInfo({ oss_key })
                              }
                            )
                          }
                          return flag
                        }}
                        onDrop={(e) => onDrop(e.dataTransfer.files?.[0] as any)}
                      >
                        <p className="ant-upload-drag-icon">
                          <CloudUploadOutlined />
                        </p>
                        <p className="ant-upload-text">{translate('create_video.upload_audio_drive')}</p>
                        <p className="ant-upload-hint">{translate('create_video.drag_or_click')}</p>
                      </Dragger>
                    </CheckLogin>
                  )}

                  {currentStep === 1 && (
                    <div className="step-progress">
                      <div className="step-progress-content">
                        <div className="percent">{percent}%</div>
                        <Progress percent={percent} showInfo={false} />
                        <div className="tips">{translate('create_video.audio_uploading')}</div>
                      </div>
                      <div className="btns">
                        <Button onClick={restartUpload}>{translate('create_video.cancel')}</Button>
                      </div>
                    </div>
                  )}

                  {currentStep === 2 && (
                    <div className="step-view">
                      <div className="step-view-box">
                        <audio controls src={audioUrl} />
                        <div className="trash" onClick={restartUpload}>
                          <DeleteOutlined />
                        </div>
                      </div>
                      <div className="btns" onClick={restartUpload}>
                        <div>{translate('create_video.reupload')}</div>
                      </div>
                    </div>
                  )}
                </div>
                {currentStep === 0 && <AudioDemos onSelect={selectDemoAudio} />}
              </div>
            </div>
          </div>

          <div className="footer-btn">
            <Dropdown
              placement="top"
              overlayClassName="highsetting-dropdown"
              trigger={['click']}
              open={highsettingOpen}
              dropdownRender={() => {
                return <CreateVideoSetting openPlan={openPlan} />
              }}
              onOpenChange={(open) => {
                setHighsettingOpen(open)
              }}
            >
              <div className="setting">
                <img src={IconSetting} />
                <span>{translate('create_video.advanced_settings')}</span>
              </div>
            </Dropdown>

            <div className="right" ref={guideRef}>
              {isBulk && (
                <CheckLogin onLoginSuccess={updateInfos}>
                  <Button
                    disabled={loading}
                    className={highSetting.mode === 1 ? 'btn-vip-solid' : 'primary-solid'}
                    loading={loading}
                    color="primary"
                    variant="outlined"
                    onClick={() => {
                      submitRef.current = ''
                      submitForm()
                    }}
                  >
                    <span>
                      {translate('create_video.generate_complete_work', {
                        count: 1,
                        noPoints: highSetting.mode === 1 ? `(${translate('create_video.no_points_consumed')})` : ''
                      })}
                    </span>
                  </Button>
                </CheckLogin>
              )}

              {systemGuideStep === 5 ? (
                <Button
                  disabled={loading}
                  className={highSetting.mode === 1 ? 'btn-vip' : ''}
                  loading={loading}
                  type="primary"
                  onClick={() => {
                    submitForm()
                  }}
                >
                  {translate('create_video.submit')}
                </Button>
              ) : (
                <CheckLogin onLoginSuccess={updateInfos}>
                  <Button
                    disabled={loading}
                    className={highSetting.mode === 1 ? 'btn-vip' : ''}
                    loading={loading}
                    type="primary"
                    onClick={() => {
                      submitRef.current = isBulk ? 'bulk' : ''
                      submitForm()
                    }}
                  >
                    {isBulk ? (
                      <label>{translate('create_video.batch_generate', { count: segments.length })}</label>
                    ) : (
                      translate('create_video.submit')
                    )}

                    {highSetting.mode === 1 && `(${translate('create_video.no_points_consumed')})`}

                    {isBulk && highSetting.mode !== 1 && (
                      <div className="btn-tag">
                        <span className="gradient">{translate('create_video.premium_exclusive')}</span>
                      </div>
                    )}
                  </Button>
                </CheckLogin>
              )}
            </div>
          </div>
        </div>

        <div id="right" className="right">
          <VideoList onRecreate={restoreData} ref={videoListRef} />
        </div>
      </div>

      <VoiceSettingModal
        preview={settingPreview}
        onCancel={() => setSettingPreview(undefined as any)}
        onOk={(voice_parameters: any) => {
          setSettingPreview(undefined as any)
          updateVoiceSetting(voice_parameters)
        }}
      />

      <VoiceModal
        open={voiceModalOpen}
        selectTts={currentTts}
        onCancel={() => setVoiceModalOpen(false)}
        upgradePlan={showPlanModal}
        onSelect={onVoiceChange}
      />

      <AvatarModal
        selectAvatar={currentAvatar}
        open={avatarModalOpen}
        onCancel={() => setAvatarModalOpen(false)}
        onSelect={(avatar: any, naturalSize: any) => onAvatarChange(avatar, naturalSize)}
      />

      <PayPointModal open={payPointModalOpen} onCancel={() => setPayPointModalOpen(false)} />

      <SystemGuide
        updateAvatar={(avatar) => onAvatarChange(avatar, true)}
        updateVoice={onVoiceChange}
        updateText={onTextChange}
      />

      <Tour
        open={systemGuideStep === 5}
        closeIcon={null}
        steps={steps}
        mask={{
          color: 'rgba(0, 0, 0, 0.7)'
        }}
      />

      <CaptionModal
        ref={captionModalRef}
        avatar={currentAvatar}
        caption={caption}
        open={captionModalOpen}
        onCancel={() => setCaptionModalOpen(false)}
        updateCaption={oncCaptchaChange}
      />

      <BackgroundModal
        avatar={currentAvatar}
        open={backgroundModalOpen}
        onCancel={() => setBackgroundModalOpen(false)}
        updateAvatar={onAvatarChange}
      />
    </div>
  )
}

export default CreateVideo
